import React, { useState, useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Form, Button, Alert } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import Message from '../components/Message'
import Loader from '../components/Loader'
import FormContainer from '../components/FormContainer'
import { resetPassword, resetPasswordState } from '../slices/userPasswordSlice'
import PasswordInput from '../components/PasswordInput'

const ResetPasswordScreen = () => {
  const { token } = useParams()
  const history = useHistory()
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordV, setNewPasswordV] = useState('')
  const [validationErrors, setValidationErrors] = useState([])
  const [passwordsMatch, setPasswordsMatch] = useState(true)

  const dispatch = useDispatch()

  const userPassword = useSelector((state) => state.userPassword)
  const { loading, successReset, error } = userPassword || {}

  // Password validation rules
  const validatePassword = (password) => {
    const errors = []
    if (password.length < 8) {
      errors.push('At least 8 characters long')
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('At least one uppercase letter')
    }
    if (!/[a-z]/.test(password)) {
      errors.push('At least one lowercase letter')
    }
    if (!/[0-9]/.test(password)) {
      errors.push('At least one number')
    }
    if (!/[!@#$%^&*()_+~`|}{[\]:;?><,./-=]/.test(password)) {
      errors.push('At least one special character')
    }
    return errors
  }

  // Check password requirements on change
  useEffect(() => {
    if (newPassword) {
      const errors = validatePassword(newPassword)
      setValidationErrors(errors)
    } else {
      setValidationErrors([])
    }
  }, [newPassword])

  // Check if passwords match
  useEffect(() => {
    if (newPasswordV) {
      setPasswordsMatch(newPassword === newPasswordV)
    } else {
      setPasswordsMatch(true)
    }
  }, [newPassword, newPasswordV])

  // Redirect to login after successful password reset
  useEffect(() => {
    if (successReset) {
      const timer = setTimeout(() => {
        dispatch(resetPasswordState())
        history.push('/login')
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [successReset, history, dispatch])

  const submitHandler = (e) => {
    e.preventDefault()
    
    if (validationErrors.length > 0) {
      return
    }
    
    if (newPassword !== newPasswordV) {
      setPasswordsMatch(false)
      return
    }
    
    dispatch(resetPassword({ token, newPassword }))
  }

  const isSubmitDisabled = 
    loading || 
    !newPassword || 
    !newPasswordV || 
    validationErrors.length > 0 || 
    !passwordsMatch ||
    successReset

  return (
    <FormContainer>
      <h1>Reset Password</h1>
      
      {successReset && (
        <Alert variant="success">
          <Alert.Heading>✓ Password Reset Successful!</Alert.Heading>
          <p>Your password has been successfully changed. Redirecting to login page...</p>
        </Alert>
      )}
      
      {error && <Message variant='danger'>{error}</Message>}
      {loading && <Loader />}
      
      {!successReset && (
        <Form onSubmit={submitHandler}>
          <PasswordInput
            id='newPassword'
            label='New Password'
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete='new-password'
          />

          {/* Password Requirements Display */}
          {newPassword && (
            <Alert variant={validationErrors.length === 0 ? 'success' : 'warning'} className="mt-2">
              <small>
                <strong>Password must contain:</strong>
                <ul className="mb-0 mt-1">
                  <li style={{ color: newPassword.length >= 8 ? 'green' : 'inherit' }}>
                    {newPassword.length >= 8 ? '✓' : '○'} At least 8 characters
                  </li>
                  <li style={{ color: /[A-Z]/.test(newPassword) ? 'green' : 'inherit' }}>
                    {/[A-Z]/.test(newPassword) ? '✓' : '○'} At least one uppercase letter
                  </li>
                  <li style={{ color: /[a-z]/.test(newPassword) ? 'green' : 'inherit' }}>
                    {/[a-z]/.test(newPassword) ? '✓' : '○'} At least one lowercase letter
                  </li>
                  <li style={{ color: /[0-9]/.test(newPassword) ? 'green' : 'inherit' }}>
                    {/[0-9]/.test(newPassword) ? '✓' : '○'} At least one number
                  </li>
                  <li style={{ color: /[!@#$%^&*()_+~`|}{[\]:;?><,./-=]/.test(newPassword) ? 'green' : 'inherit' }}>
                    {/[!@#$%^&*()_+~`|}{[\]:;?><,./-=]/.test(newPassword) ? '✓' : '○'} At least one special character
                  </li>
                </ul>
              </small>
            </Alert>
          )}

          <PasswordInput
            id='newPasswordV'
            label='Confirm New Password'
            value={newPasswordV}
            onChange={(e) => setNewPasswordV(e.target.value)}
            autoComplete='new-password'
          />

          {/* Password Match Indicator */}
          {newPasswordV && !passwordsMatch && (
            <Alert variant="danger" className="mt-2">
              <small>Passwords do not match</small>
            </Alert>
          )}
          
          {newPasswordV && passwordsMatch && newPassword && (
            <Alert variant="success" className="mt-2">
              <small>✓ Passwords match</small>
            </Alert>
          )}

          <Button 
            type='submit' 
            variant='primary' 
            disabled={isSubmitDisabled}
            className="mt-3"
          >
            Reset Password
          </Button>
        </Form>
      )}
    </FormContainer>
  )
}

export default ResetPasswordScreen